import {
  Box,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type ReactNode } from "react";

import { WasteCodeChipInput } from "../Submissions/Stepper/steps/WasteCodeChipInput";
import { formatFieldValue } from "./DiscrepancyReviewDialog.helpers";
import { type EManifestBox18a, EManifestBox18aCaption } from "./EManifestBox18aCaption";
import { type ResolveActionOption } from "./ResolveDiscrepanciesDialog";
import {
  isWasteCodeField,
  nonPositiveQuantityReason,
  type Q1Answer,
  type ResolveRow,
} from "./resolveWithValue.helpers";
import { isQ1Answer } from "./resolveWithValue.values";

// Plain radio rows, like the app's other modals: no outline per option.
export const OPTION_BOX_SX = {
  mx: 0,
  py: 0.25,
} as const;

/** Non-blocking warning lines under a resolve dialog's copy choices. */
export function SaveWarnings({ warnings }: { warnings: readonly string[] }) {
  return warnings.map((warning) => (
    <Typography key={warning} variant="body2" sx={{ mt: 1 }}>
      {warning}
    </Typography>
  ));
}

function OptionCaption({ reason }: { reason: string | null }) {
  if (!reason) return null;
  return (
    <Typography variant="caption" color="text.secondary" display="block">
      {reason}
    </Typography>
  );
}

function SourceOptionLabel({
  field,
  value,
  sourceName,
  reason,
  box18a,
}: {
  field: string | undefined;
  value: string | null;
  sourceName: string;
  reason?: string | null;
  box18a?: EManifestBox18a | null;
}) {
  return (
    <Box>
      <Typography variant="body2">
        <Box component="span" fontWeight={700}>
          {sourceName}:
        </Box>{" "}
        {formatFieldValue(field ?? "", value)}
      </Typography>
      <EManifestBox18aCaption box18a={box18a} />
      <OptionCaption reason={reason ?? null} />
    </Box>
  );
}

function ManualValueInput(props: {
  field: string;
  text: string;
  codes: string[];
  disabled: boolean;
  onTextChange: (value: string) => void;
  onCodesChange: (codes: string[]) => void;
  onFocus: () => void;
}) {
  const { field, text, codes, disabled, onTextChange, onCodesChange, onFocus } = props;
  return (
    <Stack
      spacing={0.5}
      sx={{ flexGrow: 1, minWidth: 0 }}
      onClick={(e) => e.stopPropagation()}
      onFocusCapture={onFocus}
    >
      {isWasteCodeField(field) ? (
        <WasteCodeChipInput
          label="Correct value"
          codes={codes}
          onChange={onCodesChange}
          size="small"
          disabled={disabled}
        />
      ) : (
        <TextField
          label="Correct value"
          size="small"
          fullWidth
          disabled={disabled}
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          slotProps={
            field === "quantity"
              ? { input: { endAdornment: <InputAdornment position="end">lbs</InputAdornment> } }
              : undefined
          }
        />
      )}
    </Stack>
  );
}

interface OtherValueOptionProps {
  singleRow: ResolveRow | undefined;
  selected: boolean;
  disabled: boolean;
  reason: string | null;
  manualText: string;
  manualCodes: string[];
  onTextChange: (value: string) => void;
  onCodesChange: (codes: string[]) => void;
  onSelect: () => void;
}

/** A source with no value, or a quantity of 0 or less, can't be picked; say why, like the document option does. */
function sourceUnavailableReason(
  rows: readonly ResolveRow[],
  valueOf: (row: ResolveRow) => string | null | undefined,
  sourceName: string
): string | null {
  if ((rows[0] ? valueOf(rows[0]) : null) == null) return `No ${sourceName} value for this field`;
  return nonPositiveQuantityReason(rows, valueOf);
}

/** "Other value": a radio with the input on the same line; focusing the input picks it. */
function OtherValueOption(props: OtherValueOptionProps) {
  const { singleRow, selected, disabled, reason } = props;
  return (
    <Box sx={OPTION_BOX_SX}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
        <FormControlLabel
          value="SOMETHING_ELSE"
          disabled={disabled}
          sx={{ mx: 0, flexShrink: 0 }}
          control={<Radio size="small" />}
          label={
            <Typography
              variant="body2"
              fontWeight={700}
              color={disabled ? "text.disabled" : undefined}
            >
              Other value:
            </Typography>
          }
        />
        {singleRow && (
          <ManualValueInput
            field={singleRow.field}
            text={props.manualText}
            codes={props.manualCodes}
            disabled={disabled}
            onTextChange={props.onTextChange}
            onCodesChange={props.onCodesChange}
            onFocus={() => {
              if (!selected && !disabled) props.onSelect();
            }}
          />
        )}
      </Stack>
      {/* Indented past the radio so the reason sits under the label text. */}
      {reason && (
        <Box sx={{ pl: 4.75, pb: 0.5 }}>
          <OptionCaption reason={reason} />
        </Box>
      )}
    </Box>
  );
}

function SourceOption(props: { value: Q1Answer; disabled: boolean; label: ReactNode }) {
  return (
    <FormControlLabel
      value={props.value}
      disabled={props.disabled}
      sx={OPTION_BOX_SX}
      control={<Radio size="small" />}
      label={props.label}
    />
  );
}

export interface Q1OptionsProps {
  rows: ResolveRow[];
  singleRow: ResolveRow | undefined;
  answer: Q1Answer | null;
  loading: boolean;
  /** Why "Other value" is unavailable (multi-row or unsupported field), or null. */
  otherValueDisabledReason: string | null;
  /** Why "Document's value" is unavailable (no document value), or null. */
  documentDisabledReason: string | null;
  /** Null unless every targeted row is Pending Review; any other row can't take a new value answer, only dismiss/block. */
  valueAnswersDisabledReason: string | null;
  /** Why "Mark as non-discrepancy" can't be picked (e.g. the row is pending a manual resolution), or null. */
  dismissDisabledReason: string | null;
  cantDetermineOption: ResolveActionOption | undefined;
  manualText: string;
  manualCodes: string[];
  onSelectAnswer: (next: Q1Answer) => void;
  onTextChange: (value: string) => void;
  onCodesChange: (codes: string[]) => void;
}

/** Under question 1 in both resolve dialogs: the picked value is copied to the other sources. */
export const CASCADE_HELPER = "Every other source is updated to the value you pick.";

/** Under the e-Manifest line of both Preview tables: RCRAInfo is corrected by hand. */
export const E_MANIFEST_HELPER = "Update manually in RCRAInfo. If you won't, say why in the note.";

/** Question 1: which value is correct — e-Manifest, vendor, document, a typed value, or dismiss/block. Every option is always shown; unavailable ones are disabled with a reason. */
export function Q1Options(props: Q1OptionsProps) {
  const { rows, singleRow, answer, loading, cantDetermineOption } = props;
  const valueAnswersDisabled = loading || props.valueAnswersDisabledReason != null;
  const otherValueDisabled = valueAnswersDisabled || props.otherValueDisabledReason != null;
  const eManifestValue = rows[0]?.eManifestValue ?? null;
  const vendorValue = rows[0]?.vendorValue ?? null;
  const eManifestMissing = sourceUnavailableReason(rows, (r) => r.eManifestValue, "e-Manifest");
  const vendorMissing = sourceUnavailableReason(rows, (r) => r.vendorValue, "vendor");
  const field = rows[0]?.field;
  return (
    <Box>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        What is the correct value?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {CASCADE_HELPER}
      </Typography>
      <RadioGroup
        value={answer ?? ""}
        onChange={(e) => {
          if (isQ1Answer(e.target.value)) props.onSelectAnswer(e.target.value);
        }}
      >
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          <SourceOption
            value="DOCUMENT"
            disabled={valueAnswersDisabled || props.documentDisabledReason != null}
            label={
              <SourceOptionLabel
                field={field}
                value={rows[0]?.manifestDocumentValue ?? null}
                sourceName="Manifest document"
                reason={props.documentDisabledReason}
              />
            }
          />
          <SourceOption
            value="EMANIFEST"
            disabled={valueAnswersDisabled || eManifestMissing != null}
            label={
              <SourceOptionLabel
                field={field}
                value={eManifestValue}
                sourceName="e-Manifest"
                reason={eManifestMissing}
                box18a={rows[0]?.eManifestBox18a}
              />
            }
          />
          <SourceOption
            value="VENDOR"
            disabled={valueAnswersDisabled || vendorMissing != null}
            label={
              <SourceOptionLabel
                field={field}
                value={vendorValue}
                sourceName="Vendor"
                reason={vendorMissing}
              />
            }
          />
          <OtherValueOption
            singleRow={singleRow}
            selected={answer === "SOMETHING_ELSE"}
            disabled={otherValueDisabled}
            reason={props.otherValueDisabledReason}
            manualText={props.manualText}
            manualCodes={props.manualCodes}
            onTextChange={props.onTextChange}
            onCodesChange={props.onCodesChange}
            onSelect={() => props.onSelectAnswer("SOMETHING_ELSE")}
          />
          <SourceOption
            value="DISMISS"
            disabled={loading || props.dismissDisabledReason != null}
            label={
              <Box>
                <Typography variant="body2">
                  <strong>Mark as non-discrepancy</strong>
                </Typography>
                <OptionCaption reason={props.dismissDisabledReason} />
              </Box>
            }
          />
          {cantDetermineOption && (
            <SourceOption
              value="CANT_DETERMINE"
              disabled={loading}
              label={
                <Typography variant="body2">
                  <strong>Mark as blocked</strong>
                </Typography>
              }
            />
          )}
        </Stack>
      </RadioGroup>
      {props.valueAnswersDisabledReason && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {props.valueAnswersDisabledReason}
        </Typography>
      )}
    </Box>
  );
}
